import { useEffect, useState } from 'react';

/**
 * Taille de texte du mode cuisine (§4 CLAUDE.md : « corps de texte du mode
 * cuisine à 20 px minimum, réglable jusqu'à 32 px »). Le texte était fixé en
 * dur (`text-2xl`) avant la refonte.
 *
 * Préférence personnelle d'affichage, pas liée à une recette précise :
 * mémorisée en `localStorage` — persistante d'une session à l'autre, à la
 * différence de l'avancement du mode cuisine lui-même (`sessionStorage`, par
 * recette).
 *
 * Sorti de `CookMode.tsx` en US-25 : les réglages doivent pouvoir la fixer par
 * défaut sans qu'il faille ouvrir une recette et lancer une cuisson pour
 * atteindre le seul endroit où elle se règle.
 */
export const COOK_TEXT_MIN = 20; // px — doit rester égal à --text-cook (index.css)
export const COOK_TEXT_MAX = 32; // px — doit rester égal à --text-cook-max (index.css)
export const COOK_TEXT_STEP = 2;
const STORAGE_KEY = 'mlc:mode-cuisine:taille-texte';

export function readCookTextSize(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw !== null ? Number(raw) : NaN;
    if (Number.isFinite(parsed)) return Math.min(COOK_TEXT_MAX, Math.max(COOK_TEXT_MIN, parsed));
  } catch {
    // Mémorisation en confort : une lecture refusée retombe sur le minimum.
  }
  return COOK_TEXT_MIN;
}

export interface CookTextSize {
  size: number;
  increase: () => void;
  decrease: () => void;
}

export function useCookTextSize(): CookTextSize {
  const [size, setSize] = useState(readCookTextSize);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(size));
    } catch {
      // Comme ci-dessus : un échec d'écriture n'empêche pas de continuer à lire à cette taille.
    }
  }, [size]);
  return {
    size,
    increase: () => { setSize((s) => Math.min(COOK_TEXT_MAX, s + COOK_TEXT_STEP)); },
    decrease: () => { setSize((s) => Math.max(COOK_TEXT_MIN, s - COOK_TEXT_STEP)); },
  };
}
